import { useState } from 'react';
import { useAnalytics } from '@msrceng/analytics';
import { isFunction } from 'lodash';
/**
 * Wrap useState to also store in localStorage
 *
 * @constant
 * @param {string} key identifier for state in localStorage
 * @param {T|((prevState?:T)=>T)} [initialValue] Default state value, accepts either a value or a setter function.
 *
 * > As a function, this can pass the previous state for when `reset()` is called.
 * @param {string} [sitePrefix] This will default to the first segment of the URL.
 * You basically only need this if you want to write to storage on a different
 * microservice on the same portal.
 *
 * > IE: if you're at /update-guide but you want to read state from /report-an-issue
 *  TODO:  This is possibly unstable.  Seems to work for AM/RP but not for SUG.  Needs another look
 * @returns mimics useState interface
 * @template T
 */
export function useLocalStorageState(key, initialValue, sitePrefix) {
    const siteName = sitePrefix || process.env.PUBLIC_URL || '/';
    const dataName = `${siteName}-${key}`;
    const { appInsights } = useAnalytics();
    if (key === undefined || key === null || !key) {
        throw new Error('No key provided to useLocalStorage. This could result in a case where the key "undefined" gets shared by several setters.');
    }
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState(() => {
        try {
            // Get from local storage by key
            const item = window.localStorage.getItem(dataName);
            if (item !== null && item !== undefined) {
                // Parse stored json or if none return initialValue
                return JSON.parse(item);
            }
            if ((item === null || item === undefined) && !!initialValue) {
                // Allow value to be a function so we have same API as useState
                const valueToStore = isFunction(initialValue)
                    ? initialValue()
                    : initialValue;
                window.localStorage.setItem(dataName, JSON.stringify(valueToStore));
                return valueToStore;
            }
            return;
        }
        catch (err) {
            appInsights === null || appInsights === void 0 ? void 0 : appInsights.trackEvent(err);
            // If error also return initialValue
            return isFunction(initialValue) ? initialValue() : initialValue;
        }
    });
    // Return a wrapped version of useState's setter function that
    // persists the new value to localStorage.
    const setValue = value => {
        try {
            // Allow value to be a function so we have same API as useState
            const valueToStore = isFunction(value) ? value(storedValue) : value;
            // Save state
            setStoredValue(valueToStore);
            // Save to local storage
            window.localStorage.setItem(dataName, JSON.stringify(valueToStore));
        }
        catch (err) {
            appInsights === null || appInsights === void 0 ? void 0 : appInsights.trackEvent(err);
        }
    };
    const reset = () => {
        setStoredValue(initialValue);
        // Allow value to be a function so we have same API as useState
        const valueToStore = isFunction(initialValue)
            ? initialValue(storedValue)
            : initialValue;
        window.localStorage.setItem(dataName, JSON.stringify(valueToStore));
    };
    return [storedValue, setValue, reset];
}
